import type { BoxProps } from '@chakra-ui/react';
import {
  Box,
  Button,
  Flex,
  Grid,
  Input,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { Card } from '@ui/components/Card';
import Features, { features } from '@ui/components/Features';
import { LazyComponent } from '@ui/components/LazyComponent';
import { WithConnectNetwork } from '@ui/components/Wallet/WithConnectNetwork';
import type { CoinMode } from '@ui/hooks/useCoinMode';
import { COIN_MODE, CoinModeProvider } from '@ui/hooks/useCoinMode';
import { isFeatureEnabled } from '@ui/hooks/useIsFeatureEnabled';
import { useTranslations } from '@ui/i18n';
import { useVenoSDK } from '@ui/providers/VenoSDKProvider';
import type { GardenType } from '@ui/utils/garden';
import { gardenTokenDict, gardenTypes } from '@ui/utils/garden';
import { currentWallet } from '@veno-app/wallet';
import React, { Fragment, useRef } from 'react';

import GardenCard from './GardenCard';
import { GardenHarvestDrawer } from './GardenHarvestDrawer';
import { useGardenHarvestDrawer } from './GardenHarvestDrawer/useGardenHarvestDrawer';
import GardenModal from './GardenModal';
import { GardenPlantDrawer } from './GardenPlantDrawer';
import { useGardenPlantDrawer } from './GardenPlantDrawer/useGardenPlantDrawer';
import { useGardenModalStore } from './useGardenModalStore';

const tectonicEnabled = isFeatureEnabled(features.TectonicGarden);

function Garden(props: BoxProps) {
  const t = useTranslations();
  const isZksync = currentWallet.useIsZksyncChainId();

  const tierRef = useRef<HTMLInputElement | null>(null);
  const tokenIdRef = useRef<HTMLInputElement | null>(null);

  const sdk = useVenoSDK();

  const GARDEN_TABS: CoinMode[] = ['cro', 'atom', 'eth', 'tia'];
  const gardensInPanel: GardenType[] = [
    'VVS_CRO',
    'VVS_TIA',
    'FERRO',
    'TECTONIC',
  ];

  return (
    <>
      <GardenComponents />
      <Box
        px={{
          base: 4,
          desktop: 0,
        }}
        maxW={{
          base: 'auto',
          desktop: tectonicEnabled ? '1152px' : '736px',
        }}
        margin="auto"
        {...props}
      >
        <Flex
          flexDir="column"
          textAlign={{
            base: 'start',
            desktop: 'center',
          }}
        >
          <Text textStyle="h3">
            {t('{gardenName} Garden', {
              gardenName: 'Veno',
            })}
          </Text>
          <Text
            mt={{
              base: 4,
              desktop: '8px',
            }}
            textStyle={{
              base: 'bodyBold',
              desktop: 'h4Bold',
            }}
            color={{
              desktop: 'text-light',
            }}
          >
            {t('Plant')}. {t('Grow')}. {t('Harvest')}.
          </Text>
          <Text
            mt={{
              base: 1,
              desktop: '8px',
            }}
            textStyle={{
              base: 'body',
              desktop: 'h4',
            }}
            color="text-light"
          >
            {t('Deposit your tokens to earn protocol revenue in VNO')}
          </Text>
        </Flex>
        {isFeatureEnabled(features.Atom) ? (
          <>
            {gardenTokenDict[isZksync ? 'eth' : 'cro'].VVS.enabled && (
              <GardenCard
                colorTheme="default"
                coinMode={isZksync ? 'eth' : 'cro'}
                gardenType="VVS"
                maxW={{
                  desktop: '373px',
                }}
                mx="auto"
                mt={6}
              />
            )}
            <Tabs mt="36px">
              <TabList
                maxW={{
                  desktop: '560px',
                }}
                mx="auto"
              >
                {GARDEN_TABS.map((coinMode) => {
                  return (
                    <Tab key={coinMode}>
                      {t('{name} Gardens', {
                        name: COIN_MODE[coinMode].name,
                      })}
                    </Tab>
                  );
                })}
              </TabList>
              <TabPanels>
                {GARDEN_TABS.map((coinMode) => {
                  return (
                    <CoinModeProvider key={coinMode} value={coinMode}>
                      <TabPanel px="0" py="24px">
                        <WithConnectNetwork supportedCoinModes={[coinMode]}>
                          <Flex
                            flexDir={{
                              base: 'column',
                              desktop: 'row',
                            }}
                            justify="center"
                            gap={{
                              base: 6,
                              desktop: 4,
                            }}
                          >
                            {gardensInPanel.map((gardenType, i) => {
                              if (
                                !gardenTokenDict[coinMode][gardenType].enabled
                              ) {
                                return null;
                              }
                              return (
                                <Fragment key={i}>
                                  <GardenCard
                                    coinMode={coinMode}
                                    gardenType={gardenType}
                                    flexGrow="1"
                                    maxW={{
                                      desktop: '373px',
                                    }}
                                  />
                                </Fragment>
                              );
                            })}
                          </Flex>
                        </WithConnectNetwork>
                      </TabPanel>
                    </CoinModeProvider>
                  );
                })}
              </TabPanels>
            </Tabs>
          </>
        ) : (
          <Grid
            mt={6}
            templateColumns={{ base: '1', desktop: 'repeat(3, 1fr)' }}
            flexWrap="wrap"
            flexDir={{
              base: 'column',
              desktop: 'row',
            }}
            gap={4}
          >
            {gardenTypes.map((gardenType, i) => (
              <Fragment key={i}>
                <GardenCard
                  colorTheme="garden"
                  coinMode="cro"
                  gardenType={gardenType}
                  flex="1"
                />
              </Fragment>
            ))}
          </Grid>
        )}
        <Features.MintFakeCruiser>
          <Flex flexDir="column" mt={8} gap={4}>
            <Flex flexDir="column" gap={2}>
              <Text textStyle="h3">QA Tools</Text>
              <Text
                mt={2}
                textStyle={{
                  base: 'body',
                  desktop: 'h4',
                }}
                color="text-light"
              >
                Mint 4 Cruisers (1 common, 1 rare, 1 epic, 1 legendary) or Miner
                Moles, or update tier
              </Text>
            </Flex>
            <Button
              variant="primary"
              size="cta"
              onClick={() => sdk.Cruiser.mint()}
            >
              Mint 4 fake Cruisers
            </Button>
            <Button
              variant="primary"
              size="cta"
              onClick={() => sdk.MinerMole.mint()}
            >
              Mint 4 fake Miner Moles
            </Button>
            <Card
              colorTheme="garden"
              display="flex"
              flexDir="column"
              gap={4}
              m={0}
              p={2}
            >
              <Flex gap={4}>
                <Input bg="bg.main" placeholder="Token ID" ref={tokenIdRef} />
                <Input bg="bg.main" placeholder="Tier" ref={tierRef} />
              </Flex>
              <Button
                size="cta"
                variant="secondary"
                onClick={() =>
                  sdk.RarityRegistry.updateTier(
                    parseInt(tierRef.current?.value ?? ''),
                    parseInt(tokenIdRef.current?.value ?? ''),
                  )
                }
              >
                Update Tier
              </Button>
            </Card>
          </Flex>
        </Features.MintFakeCruiser>
      </Box>
    </>
  );
}

const GardenComponents = () => {
  const isGardenModalOpen = useGardenModalStore((s) => s.isOpen);
  const isGardenHarvestDrawerOpen = useGardenHarvestDrawer((s) => s.isOpen);
  const isGardenPlantDrawerOpen = useGardenPlantDrawer((s) => s.isOpen);

  return (
    <>
      <LazyComponent isOpen={isGardenModalOpen}>
        <GardenModal />
      </LazyComponent>
      <LazyComponent isOpen={isGardenHarvestDrawerOpen}>
        <GardenHarvestDrawer />
      </LazyComponent>
      <LazyComponent isOpen={isGardenPlantDrawerOpen}>
        <GardenPlantDrawer />
      </LazyComponent>
    </>
  );
};

export default Garden;
